import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

// Einstellungen für den Patienten-Self-Service (Termin absagen/verschieben
// per Link). Eigener Settings-Tab, Pattern wie der Email-Tab in SettingsPage.

interface SelfserviceSettingsData {
  enabled: boolean;
  cancelMinHours: number;
  rescheduleMinHours: number;
  maxReschedules: number;
}

const DEFAULTS: SelfserviceSettingsData = {
  enabled: true,
  cancelMinHours: 24,
  rescheduleMinHours: 24,
  maxReschedules: 2,
};

const SelfserviceSettings: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const [settings, setSettings] = useState<SelfserviceSettingsData>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    apiService.get<SelfserviceSettingsData>('/settings/selfservice')
      .then(resp => {
        if (mounted && resp.data?.success) setSettings(resp.data.data);
      })
      .catch(() => {
        if (mounted) addToast(t('settings.selfservice.errorLoad'), 'error');
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [addToast, t]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await apiService.put<SelfserviceSettingsData>('/settings/selfservice', settings);
      if (resp.data?.success) {
        setSettings(resp.data.data);
        addToast(t('settings.selfservice.saved'), 'success');
      } else {
        addToast(resp.data?.message || t('settings.errorSave'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('settings.errorSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const numField = (
    key: 'cancelMinHours' | 'rescheduleMinHours' | 'maxReschedules',
    max: number,
  ) => (
    <input
      type="number"
      min={0}
      max={max}
      value={settings[key]}
      onChange={(e) => setSettings({ ...settings, [key]: parseInt(e.target.value || '0', 10) })}
      disabled={!settings.enabled}
      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none disabled:bg-gray-50 disabled:text-gray-400"
    />
  );

  if (loading) {
    return <div className="p-6 text-gray-500">{t('settings.loading')}</div>;
  }

  return (
    <div className="max-w-3xl">
      <section className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">🔁 {t('settings.selfservice.title')}</h2>
          <p className="text-sm text-gray-600 mt-1">{t('settings.selfservice.description')}</p>
        </div>

        <div className="flex items-center space-x-2">
          <input
            id="selfservice-enabled"
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            className="h-4 w-4"
          />
          <label htmlFor="selfservice-enabled" className="text-sm text-gray-700">
            {t('settings.selfservice.enabled')}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
              {t('settings.selfservice.cancelMinHours')}
            </label>
            {numField('cancelMinHours', 168)}
            <p className="text-[11px] text-gray-500 mt-1">{t('settings.selfservice.cancelMinHoursHint')}</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
              {t('settings.selfservice.rescheduleMinHours')}
            </label>
            {numField('rescheduleMinHours', 168)}
            <p className="text-[11px] text-gray-500 mt-1">{t('settings.selfservice.rescheduleMinHoursHint')}</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
              {t('settings.selfservice.maxReschedules')}
            </label>
            {numField('maxReschedules', 10)}
            <p className="text-[11px] text-gray-500 mt-1">{t('settings.selfservice.maxReschedulesHint')}</p>
          </div>
        </div>

        <p className="text-sm text-gray-500">{t('settings.selfservice.linkHint')}</p>

        <div className="flex items-center justify-end pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-brand-primary text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
          >
            {saving ? t('settings.saving') : t('common.save')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default SelfserviceSettings;
